<template>
  <section class="clinical-questionnaire-form pb-3">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold mb-0 py-3">
          {{title}}
      </h6>

      <div class="d-flex align-items-center justify-content-end">

        <div v-if="hasUnsavedChanges">
          <small class="text-orange is-bold">
            {{$t('clinical.people.anamnesis.unsaved')}}
          </small>
        </div>

        <div v-else-if="value && value.updated_at !== null && value.updated_at !== undefined">
          <small>
            {{$t('clinical.people.anamnesis.updated_at')}}
          </small>
          <small class="is-bold">
            {{$t('clinical.people.anamnesis.updated_by', {date: getExpressiveDate(value.updated_at), name: value.updated_by ? value.updated_by.name : ''})}}
          </small>
        </div>

        <i
          v-if="collapsable"
          class="clickable p-3"
          :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
          @click="isCollapsed = !isCollapsed"
        >
        </i>
      </div>
    </div>

    <div v-if="!isCollapsed" class="mt-3">

      <form class="evollu-form" v-if="value">

        <slot name="beforeForm"></slot>

        <!-- Time Since Hearing Loss -->
        <!--<div class="row">

          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.time_since_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <date-picker
              id="date-picker-time-since-hearing-loss"
              :value="value.time_since_hearing_loss"
              @input="v => updateValue('time_since_hearing_loss', v)"
              placeholder="dd/mm/aaaa"
              :config="{maxDate: 'today'}"
              :error="formErrors.has('time_since_hearing_loss')"
              :error-messages="[$t(formErrors.get('time_since_hearing_loss'))]"
              :disabled="!editing"
            />
          </div>
        </div>-->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.time_since_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="time-since-hearing-loss"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.time_since_hearing_loss')"
              :value="value.time_since_hearing_loss"
              @input="v => updateValue('time_since_hearing_loss', v)"
              :error="formErrors.has('time_since_hearing_loss')"
              :error-messages="[$t(formErrors.get('time_since_hearing_loss'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Right Hearing Loss Type -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.right_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.hearingLossTypeOptions"
              :value="value.right_hearing_loss_type_id"
              @input="v => updateValue('right_hearing_loss_type_id', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('right_hearing_loss_type_id')">
            <small>
              {{$t(formErrors.get('right_hearing_loss_type_id'))}}
            </small>
          </div>
        </div>

        <!-- Left Hearing Loss Type -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.left_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.hearingLossTypeOptions"
              :value="value.left_hearing_loss_type_id"
              @input="v => updateValue('left_hearing_loss_type_id', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('left_hearing_loss_type_id')">
            <small>
              {{$t(formErrors.get('left_hearing_loss_type_id'))}}
            </small>
          </div>
        </div>

        <!-- Feels Hearing Loss -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.feels_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.commonOptions"
              :value="value.feels_hearing_loss"
              @input="v => updateValue('feels_hearing_loss', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('feels_hearing_loss')">
            <small>
              {{$t(formErrors.get('feels_hearing_loss'))}}
            </small>
          </div>
        </div>

        <!-- Visited Health Professional -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.visited_health_professional')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.visited_health_professional"
              @input="onInputVisitedHealthProfessional"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('visited_health_professional')">
            <small>
              {{$t(formErrors.get('visited_health_professional'))}}
            </small>
          </div>
        </div>

        <!-- Health Professional Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.health_professional_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="health-professional-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.health_professional_obs')"
              :value="value.health_professional_obs"
              @input="v => updateValue('health_professional_obs', v)"
              :error="formErrors.has('health_professional_obs')"
              :error-messages="[$t(formErrors.get('health_professional_obs'))]"
              :disabled="!editing || !value.visited_health_professional"
            />
          </div>

        </div>

        <!-- Pain in ears -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.pain_in_ears')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.pain_in_ears"
              @input="v => updateValue('pain_in_ears', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('pain_in_ears')">
            <small>
              {{$t(formErrors.get('pain_in_ears'))}}
            </small>
          </div>
        </div>

        <!-- Ear Operation -->
        <div class="row  align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.previous_ear_operation')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.previous_ear_operation"
              @input="onInputPreviousEarOperation"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('previous_ear_operation')">
            <small>
              {{$t(formErrors.get('previous_ear_operation'))}}
            </small>
          </div>

        </div>

        <!-- Ear Operation Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.previous_ear_operation_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="ear-operation-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.previous_ear_operation_obs')"
              :value="value.operation_obs"
              @input="v => updateValue('operation_obs', v)"
              :error="formErrors.has('operation_obs')"
              :error-messages="[$t(formErrors.get('operation_obs'))]"
              :disabled="!editing || !value.previous_ear_operation"
            />
          </div>
        </div>

        <!-- Takes Ear Medication -->
        <div class="row  align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.takes_medication_for_ears')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.takes_medication_for_ears"
              @input="onInputTakesMedicationForEars"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('takes_medication_for_ears')">
            <small>
              {{$t(formErrors.get('takes_medication_for_ears'))}}
            </small>
          </div>
        </div>

        <!-- Ear Medication Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.medication')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="ear-medication-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.medication')"
              :value="value.medication_obs"
              @input="v => updateValue('medication_obs', v)"
              :error="formErrors.has('medication_obs')"
              :error-messages="[$t(formErrors.get('medication_obs'))]"
              :disabled="!editing || !value.takes_medication_for_ears"
            />
          </div>

        </div>

        <!-- Recurring Otitis -->
        <div class="row  align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.recurring_otitis')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.recurring_otitis"
              @input="v => updateValue('recurring_otitis', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('recurring_otitis')">
            <small>
              {{$t(formErrors.get('recurring_otitis'))}}
            </small>
          </div>

        </div>

        <!-- Otitis While Young -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.otities_while_young')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.otities_while_young"
              @input="v => updateValue('otities_while_young', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('otities_while_young')">
            <small>
              {{$t(formErrors.get('otities_while_young'))}}
            </small>
          </div>

        </div>

        <!-- Tinnitus Location -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tinnitus_location')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.tinnitusLocationOptions.find(o => o.id === value.tinnitus_location_id)"
              :options="formOptions.tinnitusLocationOptions"
              @input="onInputTinnitusLocation"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.tinnitus_location')"
              :allow-empty="true"
              :multiple="false"
              :searchable="false"
              open-direction="bottom"
              :disabled="!editing"
              >
            </multiselect>
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-3 offset-md-4" v-if="formErrors.has('tinnitus_location_id')">
            <small>
              {{$t(formErrors.get('tinnitus_location_id'))}}
            </small>
          </div>

        </div>

        <!-- Tinnitus Type -->
        <div class="row  align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tinnitus_type_id')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.tinnitusTypeOptions"
              :value="value.tinnitus_type_id"
              @input="v => updateValue('tinnitus_type_id', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing || value.tinnitus_location_id === null || value.tinnitus_location_id === 1"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('tinnitus_type_id')">
            <small>
              {{$t(formErrors.get('tinnitus_type_id'))}}
            </small>
          </div>

        </div>

        <!-- Tinnitus Tone Type -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tinnitus_tone_type_id')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.tinnitusToneTypeOptions"
              :value="value.tinnitus_tone_type_id"
              @input="v => updateValue('tinnitus_tone_type_id', v)"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing || value.tinnitus_location_id === null || value.tinnitus_location_id === 1"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('tinnitus_tone_type_id')">
            <small>
              {{$t(formErrors.get('tinnitus_tone_type_id'))}}
            </small>
          </div>

        </div>

        <!-- Tinnitus Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.tinnitus_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="tinnitus-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.tinnitus_obs')"
              :value="value.tinnitus_obs"
              @input="v => updateValue('tinnitus_obs', v)"
              :error="formErrors.has('tinnitus_obs')"
              :error-messages="[$t(formErrors.get('tinnitus_obs'))]"
              :disabled="!editing || value.tinnitus_location_id === null || value.tinnitus_location_id === 1"
            />
          </div>
        </div>

        <!-- Has chronic diseases -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.has_chronic_diseases')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.chronic_diseases"
              @input="v => updateValue('chronic_diseases', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('chronic_diseases')">
            <small>
              {{$t(formErrors.get('chronic_diseases'))}}
            </small>
          </div>
        </div>

        <!-- Chronic Diseases Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.chronic_diseases_obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="chronic-diseases-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.chronic_diseases_obs')"
              :value="value.chronic_diseases_obs"
              @input="v => updateValue('chronic_diseases_obs', v)"
              :error="formErrors.has('chronic_diseases_obs')"
              :error-messages="[$t(formErrors.get('chronic_diseases_obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

        <!-- Works in Noisy Environments -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.works_in_noisy_environments')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.works_in_noisy_environments"
              @input="v => updateValue('works_in_noisy_environments', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('works_in_noisy_environments')">
            <small>
              {{$t(formErrors.get('works_in_noisy_environments'))}}
            </small>
          </div>

        </div>

        <!-- Hobbies with Noisy Environments -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.hobbies_with_noisy_environments')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.hobbies_with_noisy_environments"
              @input="v => updateValue('hobbies_with_noisy_environments', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('hobbies_with_noisy_environments')">
            <small>
              {{$t(formErrors.get('hobbies_with_noisy_environments'))}}
            </small>
          </div>
        </div>

        <!-- Family With Hearing Loss -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.family_with_hearing_loss')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.family_with_hearing_loss"
              @input="onInputFamilyWithHearingLoss"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('family_with_hearing_loss')">
            <small>
              {{$t(formErrors.get('family_with_hearing_loss'))}}
            </small>
          </div>

        </div>

        <!-- Family Relation -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.family_relation_type')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="formOptions.familyRelationTypeOptions.filter(o => value.family_relation_type_ids.some(id => id === o.id))"
              :options="formOptions.familyRelationTypeOptions"
              @input="val => updateValue('family_relation_type_ids', val ? val.map(v => v.id) : null)"
              :close-on-select="false"
              track-by="id"
              :customLabel="option => $t(option.name)"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.family_relation_type')"
              :allow-empty="true"
              :multiple="true"
              :searchable="false"
              open-direction="bottom"
              :disabled="!editing || !value.family_with_hearing_loss"
              >
            </multiselect>
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-3 offset-md-4" v-if="formErrors.has('family_relation_type_ids')">
            <small>
              {{$t(formErrors.get('family_relation_type_ids'))}}
            </small>
          </div>

        </div>

        <!-- Vertigo -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.vertigo')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.booleanOptions"
              :value="value.vertigo"
              @input="v => updateValue('vertigo', v)"
              :key="index"
              :option="option.value"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('vertigo')">
            <small>
              {{$t(formErrors.get('vertigo'))}}
            </small>
          </div>

        </div>

        <!-- Previous Hearing Aid Type -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.previous_hearing_aid')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 d-flex flex-column flex-sm-row">
            <va-radio-button
              v-for="(option, index) in formOptions.previousHearingAidOptions"
              :value="value.previous_hearing_aid_type_id"
              @input="onInputPreviousHearingAidType"
              :key="index"
              :option="option.id"
              :label="$t(option.name)"
              class="col-4 col-lg-3 px-0"
              :disabled="!editing"
            />
          </div>

          <div class="text-danger text-break col-12 col-sm-7 col-md-8 offset-sm-3 offset-md-4" v-if="formErrors.has('previous_hearing_aid_type_id')">
            <small>
              {{$t(formErrors.get('previous_hearing_aid_type_id'))}}
            </small>
          </div>
        </div>

        <!-- Previou Hearing Aid Brand -->
        <div class="row">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.previous_hearing_aid_brand')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="previous-hearing-aid-brand"
              :placeholder="$t('clinical.people.anamnesis.previous_hearing_aid_brand')"
              :value="value.previous_hearing_aid_brand"
              @input="v => updateValue('previous_hearing_aid_brand', v)"
              :error="formErrors.has('previous_hearing_aid_brand')"
              :error-messages="[$t(formErrors.get('previous_hearing_aid_brand'))]"
              :disabled="!editing || value.previous_hearing_aid_type_id === null || value.previous_hearing_aid_type_id === 1"
            />
          </div>
        </div>

        <!-- Previous Hearing Aid Model -->
        <div class="row">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.previous_hearing_aid_model')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="previous-hearing-aid-model"
              :placeholder="$t('clinical.people.anamnesis.previous_hearing_aid_model')"
              :value="value.previous_hearing_aid_model"
              @input="v => updateValue('previous_hearing_aid_model', v)"
              :error="formErrors.has('previous_hearing_aid_model')"
              :error-messages="[$t(formErrors.get('previous_hearing_aid_model'))]"
              :disabled="!editing || value.previous_hearing_aid_type_id === null || value.previous_hearing_aid_type_id === 1"
            />
          </div>
        </div>

        <!-- Obs -->
        <div class="row align-items-start">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.obs')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <va-input
              id="general-obs"
              type="textarea"
              :min-rows="3"
              :placeholder="$t('clinical.people.anamnesis.obs')"
              :value="value.obs"
              @input="v => updateValue('obs', v)"
              :error="formErrors.has('obs')"
              :error-messages="[$t(formErrors.get('obs'))]"
              :disabled="!editing"
            />
          </div>
        </div>

      </form>

      <div v-else class="ml-2">
        <slot name="noData">
          <span>{{$t('clinical.people.no_data')}}</span>
        </slot>
      </div>

    </div>

  </section>
</template>

<script>
import Errors from '@/utils/StandardErrors'
import DatePicker from '@/components/common/DatePicker'
import Multiselect from 'vue-multiselect'
import * as AnamnesisOptions from './AnamnesisOptions'
import CurrentLocaleMixin from '@/mixins/CurrentLocaleMixin.vue'

export default {
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: false,
      default: function () {
        return this.$t('clinical.people.anamnesis.about_hearing_loss')
      }
    }
  },
  components: {
    DatePicker,
    Multiselect
  },
  mixins: [
    CurrentLocaleMixin
  ],
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      formOptions: AnamnesisOptions,
      hasUnsavedChanges: false
    }
  },
  methods: {
    getHasUnsavedChanges: function () {
      return this.hasUnsavedChanges
    },
    setHasUnsavedChanges: function (value) {
      this.hasUnsavedChanges = value
    },
    getDefaultValue: function () {
      return {
        time_since_hearing_loss: null,
        left_hearing_loss_type_id: null,
        right_hearing_loss_type_id: null,
        feels_hearing_loss: null,
        visited_health_professional: null,
        health_professional_obs: null,
        pain_in_ears: null,
        previous_ear_operation: null,
        operation_obs: null,
        takes_medication_for_ears: null,
        medication_obs: null,
        recurring_otitis: null,
        otities_while_young: null,
        tinnitus_location_id: null,
        tinnitus_type_id: null,
        tinnitus_tone_type_id: null,
        tinnitus_obs: null,
        chronic_diseases: null,
        chronic_diseases_obs: null,
        works_in_noisy_environments: null,
        hobbies_with_noisy_environments: null,
        family_with_hearing_loss: null,
        family_relation_type_ids: [],
        vertigo: null,
        previous_hearing_aid_type_id: null,
        previous_hearing_aid_brand: null,
        previous_hearing_aid_model: null,
        obs: null
      }
    },
    updateValue: function (field, value) {
      if (this.formErrors.has(field)) {
        this.formErrors.clear(field)
      }
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)

      if (!this.hasUnsavedChanges) {
        this.hasUnsavedChanges = true
      }
    },
    bulkUpdateValue: function (value) {
      if (Object.keys(value).length > 0) {
        this.$emit('input', { ...this.value, ...value })

        Object.keys(value).forEach(key => {
          this.$emit('clearErrors', key)
        })

        if (!this.hasUnsavedChanges) {
          this.hasUnsavedChanges = true
        }
      }
    },
    onInputVisitedHealthProfessional: function (value) {
      let fields = {
        visited_health_professional: value,
      }

      if (!value) {
        fields.health_professional_obs = null
      }

      this.bulkUpdateValue(fields)
    },
    onInputPreviousEarOperation: function (value) {
      let fields = {
        previous_ear_operation: value
      }

      if (!value) {
        fields.operation_obs = null
      }

      this.bulkUpdateValue(fields)
    },
    onInputTakesMedicationForEars: function (value) {
      let fields = {
        takes_medication_for_ears: value
      }

      if (!value) {
        fields.medication_obs = null
      }

      this.bulkUpdateValue(fields)
    },
    onInputTinnitusLocation: function (value) {
      let fields = {
        tinnitus_location_id: value ? value.id : null
      }

      if (value === null || value && value.id === 1) {
        fields.tinnitus_type_id = null
        fields.tinnitus_tone_type_id = null,
        fields.tinnitus_obs = null
      }

      this.bulkUpdateValue(fields)
    },
    onInputFamilyWithHearingLoss: function (value) {
      let fields = {
        family_with_hearing_loss: value
      }

      if (!value) {
        fields.family_relation_type_ids = []
      }

      this.bulkUpdateValue(fields)
    },
    onInputPreviousHearingAidType: function (value) {
      let fields = {
        previous_hearing_aid_type_id: value
      }

      if (value === null || value && value === 1) {
        fields.previous_hearing_aid_brand = null
        fields.previous_hearing_aid_model = null
      }

      this.bulkUpdateValue(fields)
    },
    getExpressiveDate: function (value) {
      if (value) {
        if (this.$options.filters.isToday(value)) {
          return this.$t('date_helpers.today')
        } else {
          return this.$options.filters.localTime(value, `DD [${this.$t('date_helpers.day_of_month_separator')}] MMMM [${this.$t('date_helpers.day_of_month_separator')}] YYYY`, '', this.locale)
        }
      }
      return ''
    },
  },
  watch: {
    value: function (value) {
      if ((value === null || value === undefined) && this.editing) {
        this.$emit('input', { ...this.getDefaultValue() })
      }
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ...this.value })
        }
      },
      immediate: true
    }
  }
}
</script>
